import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { Dialog, DialogContent, DialogClose } from "@/components/ui/dialog";
import { useContent, useLang } from "@/context/AppContext";
import { SectionHeader, Reveal, EASE } from "@/components/ui-bits";

function GrowthChart({ data }) {
  const { t, L } = useLang();
  const [hover, setHover] = useState(null);
  const max = Math.max(...data.map((d) => Number(d.value) || 0), 1);
  return (
    <div className="border border-hairline bg-obsidian p-5" data-testid="case-study-chart">
      <p className="font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-vermilion">{t("modal.growth")}</p>
      <div className="mt-4 flex h-40 items-end gap-4 sm:gap-6">
        {data.map((d, i) => {
          const v = Number(d.value) || 0;
          const h = Math.max((v / max) * 100, 2);
          return (
            <div
              key={i}
              className="flex h-full flex-1 flex-col items-center justify-end gap-2"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              data-testid={`chart-bar-${i}`}
            >
              <span className={`font-mono text-xs font-bold transition-colors ${hover === i ? "text-vermilion" : "text-bone"}`}>{v.toLocaleString("id-ID")}</span>
              <motion.div
                className="w-full max-w-[64px] bg-vermilion/80"
                initial={{ height: 0 }}
                animate={{ height: `${h}%` }}
                transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: EASE }}
              />
              <span className="whitespace-nowrap font-mono text-[10px] uppercase tracking-wide text-sand">{L(d.label)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Selected work — editorial index: alternating rows, oversized serif numerals,
// ink-wash image treatment on hover.
export default function CaseStudies() {
  const { t, L } = useLang();
  const { content } = useContent();
  const cases = content?.case_studies || [];
  const [active, setActive] = useState(null);

  if (!cases.length) return null;

  return (
    <section id="cases" className="bg-obsidian py-20 lg:py-32" data-testid="cases-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <SectionHeader eyebrow={t("casesEyebrow")} title={t("casesTitle")} index={3} />

        <div className="mt-12 border-t border-hairline">
          {cases.map((cs, i) => (
            <Reveal key={cs.id} delay={0.05}>
              <button
                onClick={() => setActive(cs)}
                data-testid={`case-study-card-${i + 1}`}
                className="group grid w-full grid-cols-1 gap-6 border-b border-hairline py-8 text-left transition-colors duration-300 hover:bg-surface sm:grid-cols-12 sm:items-center sm:gap-8 sm:py-10 lg:px-6"
              >
                {/* index numeral */}
                <div className="hidden sm:col-span-1 sm:block">
                  <span className="font-display text-4xl font-light italic text-sand/50 transition-colors duration-300 group-hover:text-vermilion">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>

                {/* plate */}
                {cs.image_url ? (
                  <div className="relative h-44 w-full overflow-hidden border border-hairline sm:col-span-4 sm:h-40 lg:h-44">
                    <img
                      src={cs.image_url}
                      alt={cs.brand}
                      loading="lazy"
                      className="h-full w-full object-cover object-top grayscale-[55%] transition-all duration-700 group-hover:scale-[1.04] group-hover:grayscale-0"
                    />
                  </div>
                ) : (
                  <div className="flex h-40 w-full items-center justify-center border border-hairline bg-surface sm:col-span-4">
                    <span className="font-display text-6xl font-light italic text-surface-2">{cs.brand?.charAt(0)}</span>
                  </div>
                )}

                {/* story */}
                <div className="min-w-0 sm:col-span-7">
                  <p className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-vermilion">{L(cs.industry)}</p>
                  <h3 className="mt-2.5 font-display text-2xl font-medium tracking-tight text-bone sm:text-3xl">
                    {cs.brand}
                  </h3>
                  <p className="mt-2 max-w-xl text-[15px] leading-relaxed text-sand">{L(cs.title)}</p>
                  {(cs.metrics || []).slice(0, 3).length > 0 && (
                    <div className="mt-5 flex flex-wrap gap-x-8 gap-y-3">
                      {(cs.metrics || []).slice(0, 3).map((m, j) => (
                        <div key={j}>
                          <p className="font-display text-lg font-semibold text-bone">{m.value}</p>
                          <p className="mt-0.5 font-mono text-[10px] uppercase tracking-wide text-sand">{L(m.label)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="sm:col-span-12 lg:col-span-12">
                  <span className="inline-flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-bone transition-colors group-hover:text-vermilion">
                    {t("viewCase")}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(open) => !open && setActive(null)}>
        <DialogContent
          className="max-h-[85vh] max-w-3xl overflow-y-auto border border-hairline bg-paper p-0 text-bone"
          data-testid="case-study-modal-dialog"
        >
          {active && (
            <div>
              {active.image_url && (
                <div className="relative h-56 w-full sm:h-72">
                  <img src={active.image_url} alt={active.brand} className="h-full w-full object-cover object-top" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                </div>
              )}
              <DialogClose
                className="absolute right-4 top-4 z-10 border border-hairline bg-paper/95 p-2 text-bone backdrop-blur transition-colors hover:border-vermilion hover:text-vermilion"
                data-testid="case-study-modal-close-button"
              >
                <X className="h-4 w-4" />
              </DialogClose>
              <div className="p-7 sm:p-10">
                <p className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-vermilion">{L(active.industry)}</p>
                <h3 className="mt-3 font-display text-3xl font-medium tracking-tight text-bone sm:text-4xl">{active.brand}</h3>
                <p className="mt-2 text-base text-sand">{L(active.title)}</p>

                {(active.metrics || []).length > 0 && (
                  <div
                    className={`mt-8 grid grid-cols-2 border-t border-l border-hairline ${(active.metrics || []).length > 3 ? "sm:grid-cols-4" : "sm:grid-cols-3"}`}
                    data-testid="case-study-modal-metrics"
                  >
                    {active.metrics.map((m, j) => (
                      <div key={j} className="border-b border-r border-hairline p-4">
                        <p className="font-display text-2xl font-semibold text-bone">{m.value}</p>
                        <p className="mt-1 font-mono text-[10px] font-medium leading-tight uppercase tracking-wide text-sand">{L(m.label)}</p>
                      </div>
                    ))}
                  </div>
                )}

                {(active.chart_data || []).length > 0 && (
                  <div className="mt-6">
                    <GrowthChart data={active.chart_data} />
                  </div>
                )}

                <div className="mt-8 space-y-6 border-t border-hairline pt-8">
                  {[
                    ["modal.objective", active.challenge],
                    ["modal.strategy", active.strategy],
                    ["modal.execution", active.execution],
                    ["modal.result", active.result],
                  ].map(([key, value]) =>
                    L(value) ? (
                      <div key={key} className="grid gap-2 md:grid-cols-12">
                        <p className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-vermilion md:col-span-3">{t(key)}</p>
                        <p className="text-[15px] leading-relaxed text-sand md:col-span-9">{L(value)}</p>
                      </div>
                    ) : null
                  )}
                </div>

                {(active.channels || []).length > 0 && (
                  <div className="mt-8 border-t border-hairline pt-6">
                    <p className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-sand">{t("channels")}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {active.channels.map((ch, j) => (
                        <span key={j} className="border border-hairline px-3 py-1.5 font-mono text-[11px] font-semibold text-bone">
                          {ch}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
